// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
/**
 * Consentement cookies TAMA (1er parti, sans CMP tierce).
 *
 * Trois catégories seulement :
 * - necessary  : toujours actif (session, UI, popup d'authentification)
 * - diagnostics: mesure et diagnostic (Sentry erreurs, traces, replay)
 * - recaptcha  : anti-robot (uniquement si activé côté administration)
 *
 * Pas de choix = refus des non-essentiels : tant qu'aucun cookie de
 * consentement n'existe, `diagnostics` et `recaptcha` valent false.
 */

export const CONSENT_COOKIE_NAME = "tama_consent";
export const CONSENT_COOKIE_MAX_AGE_DAYS = 180;

export interface CookieConsent {
  necessary: true;
  diagnostics: boolean;
  recaptcha: boolean;
  ts: number;
  version: string;
}

export const DENY_ALL: Omit<CookieConsent, "ts" | "version"> = {
  necessary: true,
  diagnostics: false,
  recaptcha: false,
};

/** Taux de replay Sentry mémorisé localement pour être disponible au démarrage. */
const REPLAY_RATE_KEY = "tama_sentry_replay_rate";

type Listener = (consent: CookieConsent | null) => void;
const listeners = new Set<Listener>();

function readCookie(name: string): string | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

/** Consentement enregistré, ou null si le visiteur n'a pas encore choisi. */
export function getStoredConsent(): CookieConsent | null {
  try {
    const raw = readCookie(CONSENT_COOKIE_NAME);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    return {
      necessary: true,
      diagnostics: parsed.diagnostics === true,
      recaptcha: parsed.recaptcha === true,
      ts: typeof parsed.ts === "number" ? parsed.ts : Date.now(),
      version: typeof parsed.version === "string" ? parsed.version : "1",
    };
  } catch {
    return null;
  }
}

/** Consentement effectif : refus des non-essentiels tant qu'aucun choix n'est fait. */
export function getEffectiveConsent(): Omit<CookieConsent, "ts" | "version"> {
  return getStoredConsent() ?? DENY_ALL;
}

/** Le bandeau doit être proposé si aucun choix valide pour la version en cours. */
export function needsConsent(currentVersion: string): boolean {
  const stored = getStoredConsent();
  return !stored || stored.version !== currentVersion;
}

export function saveConsent(
  choice: { diagnostics: boolean; recaptcha: boolean },
  version: string,
): CookieConsent {
  const consent: CookieConsent = {
    necessary: true,
    diagnostics: choice.diagnostics,
    recaptcha: choice.recaptcha,
    ts: Date.now(),
    version,
  };
  const maxAge = CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(consent),
  )}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  listeners.forEach((listener) => listener(consent));
  return consent;
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Taux de replay administrable, lu au démarrage (0 si inconnu). */
export function getStoredReplayRate(): number {
  const raw = window.localStorage.getItem(REPLAY_RATE_KEY);
  const value = raw === null ? NaN : Number(raw);
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0;
}

export function setStoredReplayRate(rate: number): void {
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) return;
  window.localStorage.setItem(REPLAY_RATE_KEY, String(rate));
}

/** Évènement global permettant de rouvrir le panneau depuis n'importe où. */
export const OPEN_COOKIE_SETTINGS_EVENT = "tama:open-cookie-settings";

export function openCookieSettings(): void {
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
